import { MoreHorizontal, Download, RotateCw, Trash2, ArrowLeft, Star } from 'lucide-react'
import { Link, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useRetryRun, useDeleteRun } from '@/hooks/use-runs'
import { useMarkAsExample } from '@/hooks/use-examples'
import { ApiError } from '@/api/client'
import type { RunDetail } from '@/api/types'

interface RunDetailHeaderProps {
  run: RunDetail
}

/**
 * Status to badge variant mapping.
 * Matches pattern from Phase 12 components.
 */
function getStatusVariant(status: string): 'default' | 'success' | 'destructive' | 'warning' | 'secondary' {
  switch (status) {
    case 'completed':
      return 'success'
    case 'failed':
      return 'destructive'
    case 'processing':
    case 'queued':
      return 'warning'
    case 'pending':
      return 'secondary'
    default:
      return 'default'
  }
}

export function RunDetailHeader({ run }: RunDetailHeaderProps) {
  const navigate = useNavigate()
  const { mutate: retryRun, isPending: isRetrying } = useRetryRun()
  const { mutate: deleteRun, isPending: isDeleting } = useDeleteRun()
  const { mutate: markExample, isPending: isMarking } = useMarkAsExample()

  const handleDownload = () => {
    // Open download in new tab
    window.open(`/download/${run.fileId}`, '_blank')
  }

  const handleRetry = () => {
    retryRun(run.fileId)
  }

  const handleDelete = () => {
    if (confirm(`Delete run "${run.fileName}"? This action cannot be undone.`)) {
      deleteRun(run.fileId, {
        onSuccess: () => {
          navigate('/runs')
        },
      })
    }
  }

  const handleMarkAsExample = () => {
    if (!run.phenotype) return
    markExample(
      {
        phenotypeId: run.phenotype,
        fileId: run.fileId,
        fileName: run.fileName || run.originalName || run.fileId,
        ratesJson: (run.results?.rates ?? []) as Record<string, unknown>[],
        surchargesJson: (run.results?.surcharges ?? []) as Record<string, unknown>[],
        markedBy: 'user',
      },
      {
        onSuccess: () => toast.success('Run marked as gold-standard example'),
        onError: (err) => {
          const message = err instanceof ApiError && err.status === 409
            ? 'This run is already marked as an example for this phenotype'
            : 'Failed to mark as example'
          toast.error(message)
        },
      }
    )
  }

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Link to="/runs">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold">{run.fileName || run.originalName || run.fileId}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant={getStatusVariant(run.status)}>
              {run.status}
            </Badge>
            {run.workflow && (
              <Badge variant="outline">{run.workflow}</Badge>
            )}
            {run.phenotype && (
              <Badge variant="secondary">{run.phenotype}</Badge>
            )}
          </div>
        </div>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" disabled={isRetrying || isDeleting || isMarking}>
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Actions</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleDownload}>
            <Download className="mr-2 h-4 w-4" />
            Download Results
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={handleRetry}
            disabled={isRetrying || run.status === 'processing'}
          >
            <RotateCw className="mr-2 h-4 w-4" />
            {isRetrying ? 'Retrying...' : 'Retry Run'}
          </DropdownMenuItem>
          {run.status === 'completed' && run.phenotype && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleMarkAsExample}
                disabled={isMarking}
              >
                <Star className="mr-2 h-4 w-4" />
                {isMarking ? 'Marking...' : 'Mark as Example'}
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={handleDelete}
            disabled={isDeleting}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {isDeleting ? 'Deleting...' : 'Delete Run'}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
